import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Typography } from "@mui/material";
import { useDebouncedEffect } from "@wojtekmaj/react-hooks";
import type { Promisable } from "type-fest";

import type {
  DateRangeUnbranded,
  Dayjs,
  IsoDate,
  TimeRange,
  TimeRangeOnDate,
  TimeRangeOnWeekday,
} from "@thalamos/common";
import { datesInRange, END_OF_DAY, ISO_WEEKDAY } from "@thalamos/common";
import type {
  Section12AvailabilityOverrideDate,
  SetSection12AvailabilityOverrideDatesRequestUnbranded,
} from "@thalamos/aspire-common";

import type { AutoSaveStatus } from "./autoSave.js";
import { AUTO_SAVE_DELAY } from "./autoSave.js";
import type { ResolvedDay } from "./upcomingAvailability.js";
import {
  buildOverrideRequest,
  resolveWeek,
  weekRangeFrom,
} from "./upcomingAvailability.js";
import type { AvailabilityBlock } from "~/components/design-system/index.js";
import {
  AvailabilityGrid,
  AvailabilityGridLegend,
  AvailabilityGridSummary,
  blocksToChunkKeys,
  buildDatedColumns,
  buildRowStarts,
  Button,
  chunkKeySetsMatch,
  dateRangesToBlocks,
  formatWeekLabel,
  gridPointForInstant,
  renderErrorToast,
  renderSuccessToast,
  START_OF_DAY,
  startOfWeekContaining,
} from "~/components/design-system/index.js";

/**
 * How far ahead a doctor may plan. A quarter is as far as the rotas an
 * assessment is arranged around are usually settled.
 */
export const UPCOMING_WEEKS_AHEAD = 12;

/**
 * Availability is planned Monday first, as a rota is read. The same choice
 * decides which weeks are shown and which dates are fetched to fill them, so
 * everything that touches either reads it from here.
 */
export const WEEK_STARTS_ON = ISO_WEEKDAY.monday;
const DAYS_PER_WEEK = 7;
const GRANULARITY_MINUTES = 15;

/**
 * The axis covers the whole day, and must keep doing so. Reading a column back
 * drops anything outside the axis, so a narrower one would silently delete an
 * early-morning calendar block and turn that date into an override of what
 * remained.
 */
const ROW_STARTS = buildRowStarts(
  START_OF_DAY,
  END_OF_DAY,
  GRANULARITY_MINUTES,
);

/**
 * Edits that have not yet reached the server, keyed by date.
 *
 * A date belongs to exactly one week, so an entry can never be shown against
 * the wrong one however late it settles, and an edit survives the doctor
 * paging away from the week they made it in.
 */
interface EditState {
  /** Dates edited since the last save, each holding its whole new day. */
  draft: Map<IsoDate, TimeRange[]>;
  /** Exactly what the save in flight sent, standing in until it is read back. */
  inFlight: Map<IsoDate, TimeRange[]>;
}

function noEdits(): EditState {
  return { draft: new Map(), inFlight: new Map() };
}

export interface UpcomingAvailabilityCalendarProps {
  /** Frozen by the caller: a re-render must not move "now" under an edit. */
  now: Dayjs;
  defaultWeek: readonly TimeRangeOnWeekday[];
  overrides: readonly Section12AvailabilityOverrideDate[];
  saveOverrides: (
    request: SetSection12AvailabilityOverrideDatesRequestUnbranded,
  ) => Promisable<void>;
  deleteOverrides: (range: DateRangeUnbranded) => Promisable<void>;
  /** Re-reads the overrides; awaited, so a save is not forgotten too early. */
  onSaved: () => Promisable<void>;
  onAutoSaveStatus?: (status: AutoSaveStatus) => Promisable<void>;
  /**
   * Filled with a function that saves any pending edit at once, so a caller
   * about to rewrite what this calendar shows can let the edit land first.
   */
  flushRef?: RefObject<(() => Promise<void>) | null>;
  "data-testid"?: string;
}

/** The cells one day's blocks occupy, for comparing two versions of that day. */
function cellKeysFor(
  blocks: readonly TimeRange[],
  columnIndex: number,
): Set<string> {
  return blocksToChunkKeys(
    blocks.map((block) => ({ ...block, columnIndex })),
    ROW_STARTS,
    GRANULARITY_MINUTES,
    DAYS_PER_WEEK,
  );
}

/**
 * A doctor's next few weeks, one week at a time, with every date showing what
 * the layer that decided it offers. Editing a date paints an override over
 * whatever that layer said, saved automatically a moment after the doctor stops.
 */
export function UpcomingAvailabilityCalendar({
  now,
  defaultWeek,
  overrides,
  saveOverrides,
  deleteOverrides,
  onSaved,
  onAutoSaveStatus,
  flushRef,
  "data-testid": dataTestId = "upcoming-availability-calendar",
}: UpcomingAvailabilityCalendarProps) {
  const firstWeekStart = useMemo(
    () => startOfWeekContaining(now, WEEK_STARTS_ON),
    [now],
  );
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = useMemo(
    () => firstWeekStart.add(weekOffset, "week"),
    [firstWeekStart, weekOffset],
  );
  const weekRange = useMemo(() => weekRangeFrom(weekStart), [weekStart]);

  // The ref is what the save reads, because a save runs long after the render
  // that started it; the state is what the grid renders. Every change goes
  // through `applyEdit`, so the two cannot drift.
  const editRef = useRef<EditState>(noEdits());
  const [edit, setEdit] = useState<EditState>(() => editRef.current);
  // A save started on the way out still runs to completion, but has nothing
  // left to render into, so it updates the ref alone.
  const mountedRef = useRef(true);
  const applyEdit = useCallback((next: EditState) => {
    editRef.current = next;
    if (mountedRef.current) {
      setEdit(next);
    }
  }, []);

  const overridesRef = useRef(overrides);
  useEffect(() => {
    overridesRef.current = overrides;
  }, [overrides]);

  // The ref guards against a second save starting while one is in flight; the
  // state is what lets the autosave effect run again once it has finished.
  const isSavingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const awaitingRefreshRef = useRef(false);

  // Once fresh overrides arrive, they are the authority and the stand-in goes.
  // A refetch that failed leaves what was saved on screen, which is a truer
  // picture than reverting to overrides known to be out of date.
  useEffect(() => {
    if (!awaitingRefreshRef.current) {
      return;
    }
    awaitingRefreshRef.current = false;
    if (editRef.current.inFlight.size > 0) {
      applyEdit({ draft: editRef.current.draft, inFlight: new Map() });
    }
  }, [overrides, applyEdit]);

  const reportStatus = useCallback(
    (status: AutoSaveStatus) => {
      void onAutoSaveStatus?.(status);
    },
    [onAutoSaveStatus],
  );

  /**
   * Sends whatever is drafted, one save at a time. Returns whether this call
   * completed a save, so a caller can say so only when it really happened.
   *
   * A call while a save is in flight does nothing: the draft it would have sent
   * is still there, and the autosave effect picks it up when the flight ends.
   */
  const saveDraft = useCallback(async (): Promise<boolean> => {
    if (isSavingRef.current) {
      return false;
    }
    const sent = editRef.current.draft;
    if (sent.size === 0) {
      return false;
    }

    isSavingRef.current = true;
    if (mountedRef.current) {
      setSaving(true);
    }
    // Merged rather than replaced: a date saved by an earlier flush still needs
    // its stand-in until the props catch up, even though this one leaves it out.
    applyEdit({
      draft: new Map(),
      inFlight: new Map([...editRef.current.inFlight, ...sent]),
    });
    reportStatus("saving");

    let saved = false;
    try {
      await saveOverrides(
        buildOverrideRequest(
          sent,
          overridesRef.current,
          editRef.current.inFlight,
        ),
      );
      awaitingRefreshRef.current = true;
      await onSaved();
      reportStatus("saved");
      saved = true;
    } catch {
      // The draft is the only copy of an edit that never reached the server, so
      // a failure hands it back rather than dropping it. Anything painted while
      // the save was running is newer and wins.
      const restored = new Map(sent);
      for (const [date, blocks] of editRef.current.draft) {
        restored.set(date, blocks);
      }
      applyEdit({ draft: restored, inFlight: new Map() });
      reportStatus("error");
    } finally {
      isSavingRef.current = false;
      if (mountedRef.current) {
        setSaving(false);
      }
    }
    return saved;
  }, [applyEdit, onSaved, reportStatus, saveOverrides]);

  // The latest `saveDraft`, for callers that outlive the render they were
  // created in.
  const saveDraftRef = useRef(saveDraft);
  useEffect(() => {
    saveDraftRef.current = saveDraft;
  }, [saveDraft]);

  // Autosave: once the draft has been left alone for the delay, send it. A
  // save finishing re-arms this, so an edit made during the flight is not left
  // waiting for the next one.
  useDebouncedEffect(
    () => {
      void saveDraftRef.current();
    },
    [edit.draft, saving],
    AUTO_SAVE_DELAY,
  );

  useEffect(() => {
    if (!flushRef) {
      return;
    }
    flushRef.current = async () => {
      await saveDraft();
    };
    return () => {
      flushRef.current = null;
    };
  }, [flushRef, saveDraft]);

  // Nothing here has a save button, so leaving the page must save the edit
  // rather than discard it.
  useEffect(() => {
    // Set on the way in as well as cleared on the way out, so a mount that is
    // rehearsed and rerun (as StrictMode does) is not left believing it ended.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const saveOnTheWayOut = saveDraftRef.current;
      // Deferred out of the unmount itself: a save reports its progress, and
      // telling the surrounding page that mid-teardown is not allowed.
      setTimeout(() => {
        void saveOnTheWayOut();
      }, 0);
    };
  }, []);

  const resolved = useMemo(
    () => resolveWeek(weekStart, defaultWeek, overrides),
    [weekStart, defaultWeek, overrides],
  );

  const displayed = useMemo<ResolvedDay[]>(
    () =>
      resolved.map((day) => {
        const pending = edit.draft.get(day.date) ?? edit.inFlight.get(day.date);
        return pending === undefined
          ? day
          : { date: day.date, source: "manual", blocks: pending };
      }),
    [resolved, edit],
  );

  const columns = useMemo(
    () => buildDatedColumns(weekStart, { highlightDate: now }),
    [weekStart, now],
  );

  const value = useMemo(() => {
    const dated: TimeRangeOnDate[] = displayed.flatMap((day) =>
      day.blocks.map((block) => ({ date: day.date, ...block })),
    );
    return dateRangesToBlocks(dated, weekStart);
  }, [displayed, weekStart]);

  const handleChange = useCallback(
    (next: AvailabilityBlock[]) => {
      const draft = new Map(editRef.current.draft);
      let anyChanged = false;

      displayed.forEach((day, columnIndex) => {
        const forColumn = next.filter(
          (block) => block.columnIndex === columnIndex,
        );
        const changed = !chunkKeySetsMatch(
          blocksToChunkKeys(
            forColumn,
            ROW_STARTS,
            GRANULARITY_MINUTES,
            DAYS_PER_WEEK,
          ),
          cellKeysFor(day.blocks, columnIndex),
        );
        if (!changed) {
          return;
        }
        draft.set(
          day.date,
          forColumn.map((block) => ({ from: block.from, until: block.until })),
        );
        anyChanged = true;
      });

      if (!anyChanged) {
        return;
      }
      applyEdit({ draft, inFlight: editRef.current.inFlight });
      reportStatus("saving");
    },
    [applyEdit, displayed, reportStatus],
  );

  const goToWeek = useCallback(
    (offset: number) => {
      void saveDraft();
      setWeekOffset(offset);
    },
    [saveDraft],
  );

  const markWeekUnavailable = useCallback(async () => {
    const away = new Map<IsoDate, TimeRange[]>(
      datesInRange(weekRange).map((date) => [date, []]),
    );
    applyEdit({ draft: away, inFlight: editRef.current.inFlight });
    if (await saveDraft()) {
      renderSuccessToast({
        message: `Week of ${weekStart.format("D MMM")} marked unavailable.`,
      });
    }
  }, [applyEdit, saveDraft, weekRange, weekStart]);

  const clearWeekOverrides = useCallback(async () => {
    applyEdit(noEdits());
    reportStatus("saving");
    try {
      await deleteOverrides(weekRange);
      awaitingRefreshRef.current = true;
      await onSaved();
      reportStatus("saved");
      renderSuccessToast({
        message: `Override for week of ${weekStart.format("D MMM")} removed.`,
      });
    } catch {
      reportStatus("error");
      renderErrorToast({
        message: "Failed to remove the override for this week.",
      });
    }
  }, [applyEdit, deleteOverrides, onSaved, reportStatus, weekRange, weekStart]);

  const hasOverride = displayed.some((day) => day.source === "manual");

  return (
    <Box data-testid={dataTestId}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            // On a phone the week has a row of its own, so the arrows mark its edges.
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <IconButton
            size="small"
            aria-label="Previous week"
            disabled={weekOffset === 0}
            onClick={() => {
              goToWeek(weekOffset - 1);
            }}
            data-testid="upcoming-availability-previous-week"
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography
            variant="subtitle1"
            data-testid={`${dataTestId}-week`}
            // Wide enough for the longest label (a week spanning two years), so
            // the arrows stay put and can be clicked repeatedly without chasing.
            // On a phone the arrows are pinned to the row's ends instead, and a
            // small phone's row is narrower than that label.
            sx={{
              minWidth: { xs: 0, sm: "24ch" },
              flex: { xs: 1, sm: "none" },
              textAlign: "center",
            }}
          >
            {formatWeekLabel(weekStart)}
          </Typography>
          <IconButton
            size="small"
            aria-label="Next week"
            disabled={weekOffset >= UPCOMING_WEEKS_AHEAD - 1}
            onClick={() => {
              goToWeek(weekOffset + 1);
            }}
            data-testid="upcoming-availability-next-week"
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Button
            variant="outlined"
            small
            label="Return week to default"
            // A small button has a fixed height, so a label must not wrap
            // within it. On a phone the actions stretch to fill their row.
            sx={{ whiteSpace: "nowrap", flex: { xs: "1 1 auto", sm: "none" } }}
            disabled={!hasOverride}
            onClick={() => {
              void clearWeekOverrides();
            }}
            testId="upcoming-availability-return-week-to-default"
          />
          <Button
            variant="outlined"
            small
            label="Mark week unavailable"
            sx={{ whiteSpace: "nowrap", flex: { xs: "1 1 auto", sm: "none" } }}
            onClick={() => {
              void markWeekUnavailable();
            }}
            testId="upcoming-availability-mark-week-unavailable"
          />
        </Box>
      </Box>

      <AvailabilityGrid
        columns={columns}
        value={value}
        onChange={handleChange}
        granularityMinutes={GRANULARITY_MINUTES}
        shadeBefore={gridPointForInstant(weekStart, now)}
      />

      <Box sx={{ mt: 1.5 }}>
        <AvailabilityGridLegend
          swatches={["available", "unavailable", "past"]}
        />
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <AvailabilityGridSummary
          columns={columns}
          value={value}
          data-testid="upcoming-availability-summary"
        />
      </Box>
    </Box>
  );
}
